import Store from 'store/store'
import wrap from 'word-wrapper'
import drapeau from 'drapeau'
import { Component } from 'utils/jsx'

import BoundingBox from 'abstractions/BoundingBox'
import Rectangle from 'abstractions/Rectangle'
import Text from 'components/paper/Text'

import lastOf from 'utils/array-last'

export default class Paragraph extends Component {
  static charWidth ({ font, fontSize }) {
    return fontSize * (font.defaultWidthX / font.unitsPerEm)
  }

  beforeRender ({
    x = 0, // unitless
    y = 0, // unitless
    height = 0, // unitless
    width = 0, // unitless,
    origin = 'NW',
    font = Store.poster.fonts.current['Space Mono'],
    minFontSize = 10,
    maxFontSize = 72,
    fontSize = undefined, // unitless, will be computed using `drapeau` if falsy
    lineGap = 1.05, // em
    verticalOffset = -0.28 // em
  } = {}) {
    const rectangle = new Rectangle({ x, y, width, height, origin })

    this.state = {
      value: (this.props.children[0] || '').trim(),
      font,
      minFontSize,
      maxFontSize,
      fontSize,
      lineGap,
      verticalOffset,
      rectangle,
      wrappingAlgorithm: fontSize ? 'word-wrapper' : 'drapeau'
    }

    // Avoid unnecessary computation if the paragraph is empty
    if (!this.state.value) return

    // Compute this.state.lines based on wrappingAlgorithm
    switch (this.state.wrappingAlgorithm) {
      case 'drapeau': {
        const result = drapeau(this.state.value, {
          step: 1,
          minFontSize,
          maxFontSize,
          width: this.state.rectangle.width,
          measure: (_, fontSize) => ({
            width: Paragraph.charWidth({ font: this.state.font, fontSize }),
            height: fontSize * lineGap
          }),
          penalties: penalties => {
            delete penalties.WIDOWS
            delete penalties.EOL_SHORT_WORDS
            delete penalties.UNBALANCED_LINES

            penalties.HEIGHT_DIFFERENCE = {
              weight: 0.6,
              value: ({ lines, fontSize }) => {
                const totalHeight = (verticalOffset * fontSize) + lines.reduce((sum, line) => sum + line.height, 0)
                const deltaHeight = (this.state.rectangle.height - totalHeight) / this.state.rectangle.height
                return 1 - deltaHeight
              },
              illegal: ({ lines, fontSize }) => {
                const totalHeight = (verticalOffset * fontSize) + lines.reduce((sum, line) => sum + line.height, 0)
                const deltaHeight = (this.state.rectangle.height - totalHeight) / this.state.rectangle.height
                return deltaHeight < 0
              }
            }

            penalties.OUT_OF_SQUARE = {
              weight: 0.5,
              value: ({ lines, fontSize }) => {
                const maxWidth = Math.max(...lines.map(line => line.width))
                const textHeight = lines.length * fontSize
                const ratio = textHeight / maxWidth
                return ratio > 1 ? 1 / ratio : ratio
              }
            }

            return penalties
          }
        })

        if (!result.candidates.length) break

        this.state.score = result.score
        this.state.lines = result.lines.map(line => line.value)
        this.state.fontSize = result.fontSize
        break
      }

      default:
      case 'word-wrapper': {
        const CHAR_WIDTH = Paragraph.charWidth({ font: this.state.font, fontSize: this.state.fontSize })
        const width = Math.floor(this.state.rectangle.width / CHAR_WIDTH)
        this.state.lines = wrap(this.state.value.replace(/\u00A0/g, '&nbsp;'), { width }).split('\n').map(l => l.replace(/&nbsp;/g, ' '))
        break
      }
    }
  }

  template (props) {
    const { x, y: ystart, width, height, origin } = this.state.rectangle
    if (width < 0 || height < 0) return
    if (!this.state.lines || !this.state.lines.length) return

    const offy = this.state.verticalOffset * this.state.fontSize
    const lines = this.state.lines.map((value, index) => (
      <Text
        x={x}
        y={ystart + offy + (index + 1) * this.state.lineGap * this.state.fontSize}
        ref={this.refArray('lines')}
        value={value.replace(new RegExp(Store.constants.get().NBSP_SYMBOL, 'g'), ' ')}
        font={this.state.font}
        fontSize={this.state.fontSize}
        color={props.color}
      />
    ))

    if (origin.startsWith('S')) {
      // Re-position lines from the bottom of the rectangle
      lines.forEach(line => {
        line.props.y += height - (lastOf(lines).props.y - ystart)
      })
    }

    return (
      <g class='paragraph'>
        {this.state.rectangle.vnode({ class: 'guide no-export' })}
        {lines}
      </g>
    )
  }

  afterRender () {
    this.state.bbox = this.refs.lines
      ? BoundingBox.fromBBoxArray(this.refs.lines.map(t => t.state.bbox))
      : BoundingBox.empty(this.state.rectangle.x, this.state.rectangle.y)
  }
}



// WEBPACK FOOTER //
// ./src/components/paper/Paragraph.js